import { useState, useMemo } from "react";
import { Check, ChevronsUpDown, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { translateCategoryName, translateCategoryGroup, translateCategoryType, translateCategoryDescription } from "@/lib/category-i18n";
import type { Category } from "@/types/api";

const TYPE_ORDER = [
  "Income",
  "Essentials",
  "Lifestyle",
  "Growth",
  "Ventures",
  "Investments",
  "Asset",
  "Debt",
  "Transfers",
];

interface CategoryComboboxProps {
  categories: Category[];
  value: number | undefined;
  onChange: (categoryId: number) => void;
}

export function CategoryCombobox({
  categories,
  value,
  onChange,
}: CategoryComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === value),
    [categories, value]
  );

  // Group categories by type, ordered by TYPE_ORDER
  const groupedCategories = useMemo(() => {
    const groups = new Map<string, Category[]>();

    // Initialize groups in order
    TYPE_ORDER.forEach((type) => groups.set(type, []));

    // Fill groups
    categories.forEach((cat) => {
      const existing = groups.get(cat.type);
      if (existing) {
        existing.push(cat);
      } else {
        // Unknown type — append at end
        groups.set(cat.type, [cat]);
      }
    });

    // Sort categories within each group alphabetically
    groups.forEach((cats) => cats.sort((a, b) => a.name.localeCompare(b.name)));

    // Filter out empty groups
    return Array.from(groups.entries()).filter(([, cats]) => cats.length > 0);
  }, [categories]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-11 w-full justify-between bg-input-background border-border rounded-xl text-[0.9375rem] font-normal px-3.5 hover:bg-input-background"
        >
          <span
            className={cn(
              "truncate",
              !selectedCategory && "text-muted-foreground"
            )}
          >
            {selectedCategory
              ? `${selectedCategory.icon ? selectedCategory.icon + " " : ""}${translateCategoryName(t, selectedCategory)}`
              : t('categoryCombobox.search')}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command className="rounded-lg overflow-visible">
          <CommandInput placeholder={t('categoryCombobox.search')} />
          <CommandList
            style={{ maxHeight: 280 }}
            onWheel={(e) => e.nativeEvent.stopImmediatePropagation()}
          >
            <CommandEmpty>{t('categoryCombobox.noResults')}</CommandEmpty>
            {groupedCategories.map(([type, cats]) => (
              <CommandGroup key={type} heading={translateCategoryType(t, type)}>
                {cats.map((cat) => {
                  const isSelected = cat.id === value;
                  const description = translateCategoryDescription(t, cat);
                  return (
                    <CommandItem
                      key={cat.id}
                      value={`${cat.name} ${cat.group} ${cat.type}`}
                      onSelect={() => {
                        onChange(cat.id);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          isSelected ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="flex-1 flex items-center gap-2 min-w-0">
                        {cat.icon && (
                          <span className="text-base shrink-0">
                            {cat.icon}
                          </span>
                        )}
                        <span className="truncate">{translateCategoryName(t, cat)}</span>
                        {description && (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className="shrink-0 inline-flex"
                                  onClick={(e) => e.stopPropagation()}
                                  onPointerDown={(e) => e.stopPropagation()}
                                >
                                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-xs text-xs">
                                {description}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </span>
                      <span className="text-xs text-muted-foreground ml-2 shrink-0">
                        {translateCategoryGroup(t, cat.group)}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
